import { ColumnDef } from '@tanstack/react-table'
import { Payable } from '../../../../hooks'
import { formatNumber } from '../../../../utils/formatValueWithUnit'
import { formatDateCell } from '../../../../components/common/Table/utils'
import { PayablesSummaryLabelsMap } from './usePayablesSummaryLabelsMap'
import { ActionsCell } from './ActionsCell/ActionsCell'
import { getUnitSymbol } from '../../../../hooks/useApi/utils/processUsageCostResponse'
import { RefObject } from 'react'
import { Checkbox } from '../../../../components'

export const getColumns = (
    labelsMap: PayablesSummaryLabelsMap,
    data?: Payable[],
    containerRef?: RefObject<HTMLDivElement>,
    sourceContext?: 'reportDetails'
): ColumnDef<Payable>[] => {
    let columns: ColumnDef<Payable>[] = []

    if (!sourceContext) {
        columns.push({
            id: 'select',
            enableSorting: false,
            header: ({ table }) => (
                <Checkbox
                    name={`select-all-rows-checkbox`}
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                    onClick={(event) => {
                        event.stopPropagation()
                    }}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    name={`select-row-checkbox-${row.id}`}
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    onClick={(event) => {
                        event.stopPropagation()
                    }}
                />
            ),
        })
    }

    columns = [
        ...columns,
        {
            id: 'invoiceNo',
            accessorKey: 'invoiceNo',
            header: labelsMap['invoiceNo'],
        },
        {
            id: 'providerName',
            accessorKey: 'providerName',
            header: labelsMap['providerName'],
        },
        {
            id: 'billDate',
            accessorKey: 'billDate',
            header: labelsMap['billDate'],
            sortingFn: 'datetime',
            cell: formatDateCell as ColumnDef<Payable>['cell'],
        },
        {
            id: 'dueDate',
            accessorKey: 'dueDate',
            header: labelsMap['dueDate'],
            sortingFn: 'datetime',
            cell: formatDateCell as ColumnDef<Payable>['cell'],
        },
        {
            id: 'billsCount',
            accessorKey: 'billsCount',
            header: labelsMap['billsCount'],
        },
        {
            id: 'totalInvoiced',
            accessorKey: 'totalInvoiced',
            header: () => {
                if (!data || !data.length) {
                    return labelsMap['totalInvoiced']
                }

                const unit = getUnitSymbol(data[0].totalInvoiced.unit)

                return `${labelsMap['totalInvoiced']} (${unit})`
            },
            cell: ({ cell }) => {
                const value = cell.getValue() as
                    | Payable['totalInvoiced']
                    | undefined

                return value ? formatNumber(value.value) : ''
            },
        },
        {
            id: 'charges',
            accessorKey: 'charges',
            header: () => {
                if (!data || !data.length) {
                    return labelsMap['charges']
                }

                const unit = getUnitSymbol(data[0].charges.unit)

                return `${labelsMap['charges']} (${unit})`
            },
            cell: ({ cell }) => {
                const value = cell.getValue() as Payable['charges'] | undefined
                return value ? formatNumber(value.value) : ''
            },
        },
    ]

    if (!sourceContext) {
        columns.push({
            id: 'actions',
            cell: (props) => {
                return <ActionsCell {...props} containerRef={containerRef} />
            },
            enableSorting: false,
        })
    }

    return columns
}
